import { useEffect, useState } from "react";
import { Minus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * QuantityStepper — compteur −/+ pour le nombre d'exemplaires possédés d'une carte.
 *
 * Props:
 *   • cardId: string (required)
 *   • initialQuantity: number (default 0)
 *   • status: 'owned' | 'wishlist' | 'favorite' (default 'owned')
 *   • size: 'sm' | 'md' (default 'md')
 *   • onChange: (newQty: number) => void  — callback for parent (cache update)
 */
export default function QuantityStepper({
  cardId,
  initialQuantity = 0,
  status = "owned",
  size = "md",
  onChange,
}) {
  const { user } = useAuth();
  const [qty, setQty] = useState(initialQuantity);
  const [busy, setBusy] = useState(false);

  // Reset state when the card or initial qty changes (e.g. swipe-navigating cards)
  useEffect(() => setQty(initialQuantity), [cardId, initialQuantity]);

  const persist = async (next) => {
    if (!user) {
      toast.error("Connectez-vous pour gérer votre collection");
      return;
    }
    if (next === qty) return;
    setBusy(true);
    const prev = qty;
    setQty(next); // optimistic
    try {
      const { data } = await api.patch("/collection/quantity", {
        card_id: cardId,
        status,
        quantity: next,
      });
      onChange?.(data.quantity);
    } catch (err) {
      setQty(prev);
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sizes = size === "sm"
    ? { btn: "w-7 h-7", icon: "w-3 h-3", num: "min-w-[28px] text-xs px-2" }
    : { btn: "w-9 h-9", icon: "w-4 h-4", num: "min-w-[40px] text-sm px-3" };

  return (
    <div className="inline-flex items-center gap-1.5 select-none" data-testid={`qty-stepper-${cardId}`}>
      <button
        type="button"
        onClick={() => persist(Math.max(0, qty - 1))}
        disabled={busy || qty === 0}
        data-testid={`qty-decr-${cardId}`}
        aria-label="Diminuer"
        className={`${sizes.btn} flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-700 hover:border-[#E11D48] hover:text-[#E11D48] disabled:opacity-40 disabled:cursor-not-allowed transition`}
      >
        <Minus className={sizes.icon} />
      </button>
      <div
        data-testid={`qty-value-${cardId}`}
        className={`${sizes.num} h-full inline-flex items-center justify-center font-mono font-bold tracking-wider ${qty > 0 ? "text-slate-900" : "text-slate-400"}`}
      >
        {busy ? <Loader2 className={`${sizes.icon} animate-spin`} /> : qty}
      </div>
      <button
        type="button"
        onClick={() => persist(qty + 1)}
        disabled={busy || qty >= 999}
        data-testid={`qty-incr-${cardId}`}
        aria-label="Augmenter"
        className={`${sizes.btn} flex items-center justify-center rounded-xl ${qty === 0 ? "bg-[#E11D48] text-white" : "bg-white border border-slate-200 text-slate-700"} hover:bg-[#E11D48] hover:text-white hover:border-[#E11D48] disabled:opacity-40 transition`}
      >
        <Plus className={sizes.icon} />
      </button>
    </div>
  );
}
